import { billing, payments } from '@lawbee/domain';
import { Button, StatusMarker, cn } from '@lawbee/ui';
import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  BellRing,
  CalendarClock,
  ClipboardList,
  Eye,
  FileLock2,
  Landmark,
  LockKeyhole,
  MapPin,
  MessageSquareText,
  ScanSearch,
  ShieldCheck,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { PlanComparison, PricingCards, SignupButton, TrialNote } from '../../components/Pricing';
import { MarketingLayout } from '../../shell/RootPages';

// ---------------------------------------------------------------------------
// Landing
// ---------------------------------------------------------------------------

type Feature = { icon: LucideIcon; title: string; body: string; points: string[] };

const FEATURES: Feature[] = [
  {
    icon: ClipboardList,
    title: 'Intake forms per matter type',
    body: 'Family, real estate, wills, immigration and more. Each form asks only what that kind of matter needs, and embeds on your own website.',
    points: ['Ready-made forms you can adjust', 'Missing details are tracked, not lost', 'One-click follow-up link for the rest'],
  },
  {
    icon: ScanSearch,
    title: 'Ready for your conflict check',
    body: 'Every intake captures the other parties involved. LawBee searches your contacts and matters for similar names, so the check is waiting when you open the file.',
    points: ['Parties captured on every intake', 'Name-match search, including nicknames and reordering', 'The lawyer always makes the decision'],
  },
  {
    icon: MessageSquareText,
    title: 'Case status updates',
    body: 'Move a matter to its next stage and LawBee drafts a plain-language update for the client. You approve it before anything is sent.',
    points: ['Drafted from the stage change', 'Lawyer approves before sending', 'Email, and SMS on Growth'],
  },
  {
    icon: BellRing,
    title: 'Deadline and inactivity check-ins',
    body: 'Quiet files get a nudge. LawBee flags upcoming deadlines, matters with no recent activity and documents a client has not sent yet.',
    points: ['Configurable windows per firm', 'Optional gentle client reminders', 'One list of what needs attention'],
  },
  {
    icon: CalendarClock,
    title: 'Consultation booking',
    body: 'Clients pick a time that suits your calendar. Reminders go out automatically, and paid consultations are collected before the meeting.',
    points: ['Your hours, buffers and notice periods', 'Email reminders before each consult', 'Free or paid consultations'],
  },
  {
    icon: Landmark,
    title: 'Trust and operating payments, kept apart',
    body: 'Retainers, invoices and consultation fees each route to the account you choose. Clients pay by card or Canadian pre-authorized debit through Stripe.',
    points: ['Retainers to trust by default', 'Separate ledger per destination', 'Secure pay links by email'],
  },
  {
    icon: UserRound,
    title: 'A client portal',
    body: 'Clients sign in with a one-time email link to see where their matter stands, upcoming dates, documents to send and anything to pay.',
    points: ['No passwords for clients', 'Stage progress at a glance', 'Secure document upload'],
  },
];

const PRIVACY: { icon: LucideIcon; title: string; body: string }[] = [
  { icon: ShieldCheck, title: 'Never used for AI training', body: 'Client data is never used to train AI models. AI features are off unless your firm turns them on.' },
  { icon: Eye, title: 'Audit log of client-data access', body: 'Every view, export and change involving client information is recorded with who, when and from where.' },
  { icon: FileLock2, title: 'Confidential by default', body: 'Your confidentiality notice appears on every intake form and booking page. Portal links expire for privacy.' },
  { icon: MapPin, title: 'Canadian data location', body: 'Primary data is stored with a Canada/North America location hint, close to your firm and your clients.' },
];

function SectionHeading({ eyebrow, title, children, center }: { eyebrow?: string; title: string; children?: ReactNode; center?: boolean }) {
  return (
    <div className={cn('max-w-2xl space-y-3', center && 'mx-auto text-center')}>
      {eyebrow ? <p className="text-[13px] font-semibold uppercase tracking-[0.08em] text-primary">{eyebrow}</p> : null}
      <h2 className="text-[28px] font-semibold leading-tight tracking-[-0.025em] sm:text-[34px]">{title}</h2>
      {children ? <p className="text-[16px] leading-relaxed text-muted-foreground">{children}</p> : null}
    </div>
  );
}

/** A small illustrative "front desk" panel for the hero (static, no real data). */
function HeroPanel() {
  const rows: { tone: 'done' | 'progress' | 'risk' | 'idle'; title: string; meta: string }[] = [
    { tone: 'progress', title: 'New intake: separation agreement', meta: 'Parties captured, ready for conflict check' },
    { tone: 'risk', title: 'Update waiting for approval', meta: 'Stage moved to Disclosure' },
    { tone: 'done', title: 'Consultation booked', meta: 'Thu 10:30, paid' },
    { tone: 'idle', title: 'Retainer requested', meta: 'Trust account' },
  ];
  return (
    <div className="rounded-2xl bg-card p-5 shadow-card ring-1 ring-border/60 sm:p-6" aria-hidden>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm font-semibold">Today at the front desk</p>
        <span className="figures text-xs text-muted-foreground">4 items</span>
      </div>
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.title} className="flex items-start gap-3 rounded-xl bg-muted/50 px-3.5 py-3">
            <StatusMarker tone={r.tone} className="mt-0.5" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{r.title}</p>
              <p className="truncate text-xs text-muted-foreground">{r.meta}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function LandingPage() {
  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="mx-auto grid max-w-6xl items-center gap-12 px-4 pb-16 pt-12 sm:px-8 sm:pt-20 lg:grid-cols-[1.15fr_1fr] lg:pb-24">
        <div className="space-y-6">
          <p className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-[13px] font-medium text-accent-foreground">
            Built for small Canadian law firms
          </p>
          <h1 className="text-[38px] font-semibold leading-[1.08] tracking-[-0.035em] sm:text-[52px]">The front office your firm has been missing.</h1>
          <p className="max-w-xl text-[17px] leading-relaxed text-muted-foreground">
            LawBee handles intake, conflict-check preparation, client updates, consultations and payments, so your lawyers spend their time on legal work
            and your clients always know where things stand.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg">
              <Link to="/signup">
                Start free trial <ArrowRight />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/pricing">See pricing</Link>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            {billing.TRIAL_DAYS}-day free trial. No card needed. Prices in CAD, per firm.
          </p>
        </div>
        <HeroPanel />
      </section>

      {/* Features */}
      <section className="border-t bg-card/40">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-8 sm:py-24">
          <SectionHeading eyebrow="What LawBee does" title="From first enquiry to final invoice">
            Each piece works on its own and together. Start with intake and booking, and add updates, check-ins and the portal when you are ready.
          </SectionHeading>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <article key={f.title} className="flex flex-col rounded-2xl bg-card p-6 shadow-card">
                <div className="flex size-10 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                  <f.icon className="size-5" aria-hidden />
                </div>
                <h3 className="mt-4 text-[17px] font-semibold tracking-[-0.015em]">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
                <ul className="mt-4 space-y-1.5 text-sm">
                  {f.points.map((p) => (
                    <li key={p} className="flex gap-2">
                      <StatusMarker tone="done" className="mt-[3px]" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                {f.icon === Landmark ? <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{payments.TRUST_COMPLIANCE_NOTICE}</p> : null}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-8 sm:py-24">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:items-start">
          <div className="space-y-4">
            <div className="flex size-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <LockKeyhole className="size-5" aria-hidden />
            </div>
            <SectionHeading eyebrow="Privacy and confidentiality" title="Your clients trust you. You can trust LawBee.">
              Solicitor-client confidentiality shapes how LawBee is built, from what we store to who can see it.
            </SectionHeading>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {PRIVACY.map((p) => (
              <div key={p.title} className="rounded-2xl bg-card p-5 shadow-card">
                <p.icon className="size-5 text-primary" aria-hidden />
                <h3 className="mt-3 text-[15px] font-semibold">{p.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t bg-card/40">
        <div className="mx-auto max-w-5xl space-y-10 px-4 py-16 sm:px-8 sm:py-24">
          <SectionHeading eyebrow="Pricing" title="Simple pricing per firm" center>
            Two plans in Canadian dollars. No per-lawyer surprises.
          </SectionHeading>
          <PricingCards action={() => <SignupButton />} />
          <TrialNote />
          <div className="space-y-4 pt-4">
            <h3 className="text-center text-lg font-semibold">Compare plans</h3>
            <PlanComparison />
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-8 sm:py-20">
        <h2 className="text-[26px] font-semibold tracking-[-0.025em] sm:text-[30px]">Set up your front office this afternoon.</h2>
        <p className="mt-3 text-muted-foreground">Pick your practice areas, publish your first intake form and share your booking link.</p>
        <div className="mt-6 flex justify-center">
          <Button asChild size="lg">
            <Link to="/signup">
              Start free trial <ArrowRight />
            </Link>
          </Button>
        </div>
      </section>
    </MarketingLayout>
  );
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

const FAQ: { q: string; a: string }[] = [
  { q: 'Are prices in Canadian dollars?', a: 'Yes. Both plans are billed monthly in Canadian dollars, plus applicable taxes.' },
  { q: 'Is pricing per lawyer?', a: 'No. Pricing is per firm. Starter includes up to 3 team members; Growth has no limit on team members or open matters.' },
  {
    q: 'What happens after the free trial?',
    a: `Your ${billing.TRIAL_DAYS}-day trial includes every Growth feature. Choose a plan any time before it ends to keep going. If you do not, your workspace becomes read-only until you subscribe.`,
  },
  { q: 'Can I cancel any time?', a: 'Yes. There are no contracts. Cancel from Settings and your plan stays active until the end of the billing period.' },
  {
    q: 'We already use WRKRBZ Hive. Can LawBee be part of our bundle?',
    a: 'Yes. Firms on WRKRBZ Hive can add LawBee to their Hive bundle and manage billing in one place. Open LawBee from Hive to get started.',
  },
  {
    q: 'How are client payments handled?',
    a: 'Client payments are processed by Stripe and routed to your trust or operating account depending on the payment type. LawBee does not hold client funds.',
  },
];

export function PricingPage() {
  return (
    <MarketingLayout>
      <div className="mx-auto max-w-5xl space-y-10 px-4 py-12 sm:px-8 sm:py-20">
        <div className="mx-auto max-w-2xl space-y-3 text-center">
          <h1 className="text-[34px] font-semibold leading-tight tracking-[-0.03em] sm:text-[44px]">Pricing</h1>
          <p className="text-[17px] text-muted-foreground">One price per firm, in Canadian dollars. Start free and choose a plan when you are ready.</p>
        </div>
        <PricingCards action={() => <SignupButton />} />
        <TrialNote />
        <section className="space-y-4 pt-4" aria-labelledby="compare-heading">
          <h2 id="compare-heading" className="text-center text-xl font-semibold">
            Compare plans
          </h2>
          <PlanComparison />
        </section>
        <section className="mx-auto max-w-3xl space-y-4 pt-6" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="text-center text-xl font-semibold">
            Questions
          </h2>
          <div className="divide-y rounded-2xl bg-card shadow-card">
            {FAQ.map((f) => (
              <details key={f.q} className="group px-5 py-4 sm:px-6">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-medium">
                  {f.q}
                  <ArrowRight className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" aria-hidden />
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
        <div className="flex justify-center">
          <Button asChild size="lg">
            <Link to="/signup">
              Start your free trial <ArrowRight />
            </Link>
          </Button>
        </div>
      </div>
    </MarketingLayout>
  );
}
